import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Tags, Plus, Trash2, Check } from "lucide-react";

function RoleRow({ role, onChanged }) {
  const [name, setName] = useState(role.name);
  const [confirm, setConfirm] = useState(false);
  const slug = role.name.toLowerCase().replace(/\s+/g, "-");
  const pending = useRef({ name: role.name, saved: role.name });
  pending.current.name = name;

  const save = async (value) => {
    if (!value.trim() || value.trim() === pending.current.saved) return;
    try { await api.put(`/roles/${role.id}`, { name: value }); pending.current.saved = value.trim(); toast.success("Role renamed"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setName(pending.current.saved); }
  };
  const rename = () => save(name);
  useEffect(() => () => { save(pending.current.name); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = async () => {
    try {
      const { data } = await api.delete(`/roles/${role.id}`);
      pending.current.saved = pending.current.name;
      toast.success(data.staff_unassigned ? `Role removed. ${data.staff_unassigned} staff now need a role.` : "Role removed");
      onChanged();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="flex items-center gap-2" data-testid={`role-row-${slug}`}>
      <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={rename} onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
        data-testid={`role-name-input-${slug}`} className="h-9" />
      <span className="text-xs text-muted-foreground w-16 shrink-0 text-right">{role.staff_count} staff</span>
      {confirm ? (
        <Button size="sm" variant="destructive" onClick={remove} onBlur={() => setConfirm(false)} data-testid={`role-confirm-delete-${slug}`} className="shrink-0 gap-1">
          <Check className="h-3.5 w-3.5" /> Delete
        </Button>
      ) : (
        <Button size="icon" variant="ghost" onClick={() => setConfirm(true)} data-testid={`role-delete-${slug}`} className="shrink-0 text-destructive hover:text-destructive">
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export function RolesDialog({ roles, onChanged }) {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/roles", { name }); setName(""); toast.success("Role added"); onChanged(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="manage-roles-button" className="gap-2"><Tags className="h-4 w-4" /> Roles</Button>
      </DialogTrigger>
      <DialogContent data-testid="roles-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head">Roles</DialogTitle>
          <DialogDescription>Rename a role by editing it (staff and shifts update too). Deleting a role marks its staff as "Role needed".</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="New role" data-testid="new-role-input" />
          <Button onClick={add} data-testid="add-role-button" className="gap-1.5 shrink-0"><Plus className="h-4 w-4" /> Add</Button>
        </div>
        <div className="space-y-2 mt-2">
          {roles.map((r) => <RoleRow key={`${r.id}-${r.name}`} role={r} onChanged={onChanged} />)}
        </div>
      </DialogContent>
    </Dialog>
  );
}
